import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'underscore';

import connectOptions, { mergeProps } from '../../../utils/connectOptions';
import {
  handleUpdateCompany,
  saveInfo,
  closeValidationError,
  updateRetainer,
  updateCompanyRetainers,
  addCompanyRetainer,
  removeCompanyRetainer,
  findTargets,
  updateAgreementCategories,
  updateCompanyRetainerAgreementCategories,
  getCompanyRetainersByCompanyId,
  uploadAgreementFile,
  findBuyers,
  getCompanyRetainerFiles,
  deleteCompanyRetainerFile,
} from '../../../actions/company/buyer/retainer';
import { getAgreementCategories } from '../../../actions/agreementCategories';
import { getCompanyInfo } from '../../../actions/companyDetail';
import { getChanged, getIn, toJS, unwrap } from '../../../utils/ChangeSpy';
import { Retainer } from '../../../components/Company/CompanyBuyer/Retainer';
import { PrettyErrorProvider } from '../../../contexts/PrettyErrorContext';
import { uniqueNewId } from '../../../utils/uniqueId';
import { dateToString } from '../../../utils/dateFormat';
import { convertMoneyToNumber } from '../../../utils/string';
import config from '../../../config';
import stopPropagation from '../../../decorators/stopPropagation';
import confirm from '../../decorators/confirm';
import { showInformation } from '../../../utils/MessagePopup';
import { usePopupContext } from '../../../contexts/PopupContext';
import { useEntityEventContext } from '../../../contexts/EntityEventContext';

const { func, object, bool } = PropTypes;

const isValidDate = date => !!date && !!date.isValid && date.isValid();

const RetainerContainer = props => {
  const {
    match,
    company,
    retainer,
    inputErrors,
    agreementFiles,
    buyer,
    user,
    isDuplicateCompany,
    handleUpdateCompany,
    saveInfo,
    closeValidationError,
    updateCompanyRetainers,
    addCompanyRetainer,
    removeCompanyRetainer,
    getCompanyInfo,
    findTargets,
    getAgreementCategories,
    updateAgreementCategories,
    updateCompanyRetainerAgreementCategories,
    findBuyers,
    getCompanyRetainersByCompanyId,
    uploadAgreementFile,
    getCompanyRetainerFiles,
    deleteCompanyRetainerFile,
  } = props;

  const popupContext = usePopupContext();
  const { openPopup, closePopup } = popupContext;
  const { addOnSaveCallback, removeOnSaveCallback } = useEntityEventContext();

  const { companyId } = match.params;

  const canEditData = !isDuplicateCompany;
  const buyerHighStatus = Number(getIn(buyer, 'buyerHighStatus'));
  const userRole = user.getIn(['roles', 0, 'slug']);
  const isReadOnly =
    !canEditData || !(buyerHighStatus >= 9.0 && [config.FINANCE_ADMIN, config.ADMINISTRATOR].includes(userRole));
  const isHighestStatus = getIn(buyer, 'buyerHighStatus') === '10.00';

  const retainerRef = useRef(retainer);
  const companyIdRef = useRef(companyId);

  retainerRef.current = retainer;
  companyIdRef.current = companyId;

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();

      if (retainerRef.current.get('isValid')) {
        const body = getChanged(retainerRef.current);
        const companyRetainers = toJS(retainerRef.current.get('companyRetainers')).map(companyRetainer => {
          const start_date = isValidDate(companyRetainer.startDate) ? dateToString(companyRetainer.startDate) : null;
          const end_date = isValidDate(companyRetainer.endDate) ? dateToString(companyRetainer.endDate) : null;
          const notification_cancellation_date = isValidDate(companyRetainer.notificationCancellationDate)
            ? dateToString(companyRetainer.notificationCancellationDate)
            : null;
          const notification_start_date = isValidDate(companyRetainer.notificationStartDate)
            ? dateToString(companyRetainer.notificationStartDate)
            : null;
          const id = companyRetainer.id.toString().includes('NEW_') ? null : { id: companyRetainer.id };

          delete companyRetainer.startDate;
          delete companyRetainer.endDate;
          delete companyRetainer.notificationCancellationDate;
          delete companyRetainer.notificationStartDate;

          return {
            ...id,
            agreement_categories: companyRetainer.agreementCategories,
            is_source_for_agreement: companyRetainer.isSourceForAgreement,
            delete_type: companyRetainer.deleteType,
            retainer: convertMoneyToNumber(companyRetainer.retainer.toString()),
            contractual_name: companyRetainer.contractualName,
            company_id: companyRetainer.companyId || companyIdRef.current,
            type: companyRetainer.type,
            tailMonths: companyRetainer.tailMonths === '' ? null : companyRetainer.tailMonths,
            tailLastIntroduced: companyRetainer.tailLastIntroduced === '' ? null : companyRetainer.tailLastIntroduced,
            tailTermination: companyRetainer.tailTermination === '' ? null : companyRetainer.tailTermination,
            retainer_note: companyRetainer.retainerNote,
            start_date,
            end_date,
            notification_cancellation_date,
            notification_start_date,
            targetId: companyRetainer.targetId === '' ? null : companyRetainer.targetId,
          };
        });

        updateCompanyRetainers({
          companyId: companyIdRef.current,
          body: companyRetainers,
          afterSuccess: () => {
            getCompanyInfo({ companyId: companyIdRef.current });
          },
        });

        if ('agreementCategories' in body) {
          body.agreementCategories = body.agreementCategories.toJS();
        }

        if ('isSourceForAgreement' in body) {
          body.isSourceForAgreement = body.isSourceForAgreement.toJS();
        }

        if (Object.keys(body).length) {
          saveInfo({ companyId: companyIdRef.current, body });
        }
      }
    },
    [updateCompanyRetainers, getCompanyInfo, saveInfo],
  );

  useEffect(() => {
    getAgreementCategories();
    getCompanyRetainerFiles({ companyId });
    addOnSaveCallback(handleSubmit);

    return () => {
      removeOnSaveCallback(handleSubmit);
    };
  }, []);

  const handleChange = useCallback(
    ({ name, value }) => {
      if (name === 'tailMonths' && value === '') {
        handleUpdateCompany({ name, value: null });
      } else {
        handleUpdateCompany({ name, value });
      }
    },
    [handleUpdateCompany],
  );

  const handleFormChange = useCallback(
    ({ target: { name, type, value, checked } }) => {
      if (type === 'checkbox') {
        handleChange({ name, value: checked });

        return;
      }

      handleChange({ name, value });
    },
    [handleChange],
  );

  const handleSuggestionsChange = useCallback(
    ({ name, value }) => {
      handleChange({ name, value });
    },
    [handleChange],
  );

  const handleSelectAgreementCategories = useCallback(
    (agreementCategories, type, index) => {
      openPopup('AgreementCategoriesPopup', {
        agreementCategories,
        updateAgreementCategories:
          type === 'agreementCategories'
            ? updateAgreementCategories
            : agreementCategories => updateCompanyRetainerAgreementCategories(agreementCategories, index),
      });
    },
    [openPopup, updateAgreementCategories, updateCompanyRetainerAgreementCategories],
  );

  const handleAgreementLink = useCallback(
    ({ agreementCategories, type, index, selectedAgreementCategory }) => {
      openPopup('AgreementLinkPopup', {
        selectedAgreementCategory,
        agreementCategories,
        companyName:
          selectedAgreementCategory.linkedAgreementBuyerName === null
            ? unwrap(company.get('legalName', ''))
            : selectedAgreementCategory.linkedAgreementBuyerName,
        findBuyers,
        getCompanyRetainersByCompanyId,
        handleUpdateCompany,
        updateAgreementCategories:
          type === 'agreementCategories'
            ? updateAgreementCategories
            : agreementCategories => updateCompanyRetainerAgreementCategories(agreementCategories, index),
      });
    },
    [
      openPopup,
      company,
      findBuyers,
      getCompanyRetainersByCompanyId,
      handleUpdateCompany,
      updateAgreementCategories,
      updateCompanyRetainerAgreementCategories,
    ],
  );

  const handleAgreementUnlink = useCallback(
    ({ agreementCategories, type, index }) => {
      if (type === 'agreementCategories') {
        updateAgreementCategories(agreementCategories);
      } else {
        updateCompanyRetainerAgreementCategories(agreementCategories, index);
      }
    },
    [updateAgreementCategories, updateCompanyRetainerAgreementCategories],
  );

  const handleAddRetainer = useCallback(
    type => {
      const hasUnsavedRetainer = retainer
        .get('companyRetainers')
        .find(companyRetainer => typeof companyRetainer.get('id') === 'string');

      if (hasUnsavedRetainer) {
        showInformation(openPopup, 'There is an unsaved Restart Agreement. Please save it before adding a new one.');

        return;
      }

      const retainerOriginal = {
        contractualName: unwrap(retainer.get('contractualName', '')),
        agreementCategories: [],
        retainer: unwrap(retainer.get('retainer', '')),
        startDate: unwrap(retainer.get('startDate', '')),
        endDate: unwrap(retainer.get('endDate', '')),
        notificationCancellationDate: unwrap(retainer.get('notificationCancellationDate', '')),
        notificationStartDate: unwrap(retainer.get('notificationStartDate', '')),
        type,
        tailMonths: unwrap(retainer.get('tailMonths', '')),
        tailLastIntroduced: unwrap(retainer.get('tailLastIntroduced', '')),
        tailTermination: unwrap(retainer.get('tailTermination', '')),
        retainerNote: unwrap(retainer.get('retainerNote', '')),
        id: uniqueNewId(),
      };

      addCompanyRetainer(retainerOriginal);

      // We need setTimeout because we wait for new element to be rendered.
      // TODO: replace setTimeout() with a better solution.
      setTimeout(() => {
        const firstElement = document.querySelector(`.${type}-contractual-input`);

        firstElement.focus();
      }, 0);
    },
    [retainer, openPopup, addCompanyRetainer],
  );

  const handleConfirmDelete = useCallback(
    (id, sourceForAgreement) => {
      if (typeof id === 'string') {
        removeCompanyRetainer(id);
      } else {
        openPopup('DeleteRestartedAgreementPopup', {
          id,
          sourceForAgreement,
          removeCompanyRetainer,
        });
      }
    },
    [removeCompanyRetainer, openPopup],
  );

  const requestTargets = useCallback(
    ({ filter, page, afterSuccess }) => {
      findTargets({ filter, page, afterSuccess });
    },
    [findTargets],
  );

  const handleFindTargets = useMemo(() => debounce(requestTargets, 500), [requestTargets]);

  const handleAgreementUploadFile = useCallback(
    (event, companyRetainerId) => {
      event.preventDefault();

      const { target } = event;
      const formData = new FormData();

      formData.append(target.name, target.files.item(0));

      if (companyRetainerId) {
        formData.append('company_retainer_id', companyRetainerId);
      }

      uploadAgreementFile({ companyId, formData });
    },
    [companyId, uploadAgreementFile],
  );

  const deleteAgreementFileAfterConfirm = useCallback(
    (event, file) => {
      deleteCompanyRetainerFile(
        {
          id: file.get('id'),
          companyId,
        },
        () => {
          closePopup();
          getCompanyRetainerFiles({ companyId });
        },
      );
    },
    [companyId, deleteCompanyRetainerFile, getCompanyRetainerFiles, closePopup],
  );

  const handleAgreementDeleteFile = useCallback(
    stopPropagation(confirm('Delete file?', deleteAgreementFileAfterConfirm, popupContext)),
    [deleteAgreementFileAfterConfirm, popupContext],
  );

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <Retainer
        agreementFiles={agreementFiles}
        canEditData={canEditData}
        isHighestStatus={isHighestStatus}
        isReadOnly={isReadOnly}
        onAddRetainer={handleAddRetainer}
        onAgreementLink={handleAgreementLink}
        onAgreementUnlink={handleAgreementUnlink}
        onChange={handleFormChange}
        onDelete={handleConfirmDelete}
        onDeleteFile={handleAgreementDeleteFile}
        onFindTargets={handleFindTargets}
        onSelectAgreementCategories={handleSelectAgreementCategories}
        onSuggestionsChange={handleSuggestionsChange}
        onUploadFile={handleAgreementUploadFile}
        retainer={retainer}
      />
    </PrettyErrorProvider>
  );
};

RetainerContainer.propTypes = {
  inputErrors: object.isRequired,
  isDuplicateCompany: bool.isRequired,
  saveInfo: func.isRequired,
};

function mapStateToProps(state) {
  return {
    company: state.targetCompany.info.getIn(['info']),
    companyId: state.targetCompany.info.getIn(['info', 'id']),
    agreementFiles: state.targetCompany.buyer.retainer.get('agreementFiles'),
    retainer: state.targetCompany.buyer.retainer,
    inputErrors: state.targetCompany.buyer.retainer.get('inputErrors'),
    buyer: state.targetCompany.buyer.info,
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
    user: state.auth.get('user', null),
  };
}

export { RetainerContainer };
export default connect(
  mapStateToProps,
  {
    handleUpdateCompany,
    saveInfo,
    closeValidationError,
    updateRetainer,
    updateCompanyRetainers,
    addCompanyRetainer,
    removeCompanyRetainer,
    getCompanyInfo,
    findTargets,
    getAgreementCategories,
    updateAgreementCategories,
    updateCompanyRetainerAgreementCategories,
    findBuyers,
    getCompanyRetainersByCompanyId,
    uploadAgreementFile,
    getCompanyRetainerFiles,
    deleteCompanyRetainerFile,
  },
  mergeProps,
  connectOptions,
)(memo(RetainerContainer));
